import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Rating from '@mui/material/Rating'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useTranslation } from 'react-i18next'

import { contentApi } from '../../api/endpoints'
import { ErrorState } from '../../components/common/States'

/**
 * "Tell us about your stay" - open to anyone, published by nobody but the owner.
 *
 * <p>No account, no booking reference, four fields. Every hurdle in front of a form like this
 * costs reviews, and the thing that keeps the page honest is not a login: it is that a person
 * reads each one before it appears.
 */
export function ShareExperienceForm() {
  const { t } = useTranslation()

  const [authorName, setAuthorName] = useState('')
  const [authorRole, setAuthorRole] = useState('')
  const [quote, setQuote] = useState('')
  const [rating, setRating] = useState<number | null>(5)
  const [contactEmail, setContactEmail] = useState('')

  const submit = useMutation({
    mutationFn: () =>
      contentApi.submitExperience({
        authorName: authorName.trim(),
        authorRole: authorRole.trim() || undefined,
        quote: quote.trim(),
        rating: rating ?? undefined,
        contactEmail: contactEmail.trim() || undefined,
      }),
  })

  const ready = authorName.trim().length > 1 && quote.trim().length >= 20

  if (submit.isSuccess) {
    return (
      <Card elevation={0} sx={{ border: 1, borderColor: 'primary.main' }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Typography variant="h5" sx={{ mb: 1 }}>
            🙏 {t('experience.thanksTitle')}
          </Typography>
          <Typography color="text.secondary">{t('experience.thanksBody')}</Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
        <Typography variant="h5" sx={{ mb: 1 }}>
          {t('experience.title')}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {t('experience.body')}
        </Typography>

        {submit.isError && (
          <Box sx={{ mb: 2 }}>
            <ErrorState error={submit.error} />
          </Box>
        )}

        <Stack spacing={2.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('experience.yourName')}
              value={authorName}
              onChange={(event) => setAuthorName(event.target.value)}
              fullWidth
              required
            />
            <TextField
              label={t('experience.yourRole')}
              value={authorRole}
              onChange={(event) => setAuthorRole(event.target.value)}
              placeholder={t('experience.yourRolePlaceholder')}
              fullWidth
            />
          </Stack>

          <TextField
            label={t('experience.yourWords')}
            value={quote}
            onChange={(event) => setQuote(event.target.value)}
            multiline
            minRows={5}
            required
            helperText={t('experience.yourWordsHelp')}
          />

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('experience.rating')}
            </Typography>
            <Rating value={rating} onChange={(_, value) => setRating(value)} size="large" />
          </Box>

          <TextField
            label={t('experience.email')}
            type="email"
            value={contactEmail}
            onChange={(event) => setContactEmail(event.target.value)}
            helperText={t('experience.emailHelp')}
          />

          <Box>
            <Button
              variant="contained"
              size="large"
              disabled={!ready || submit.isPending}
              onClick={() => submit.mutate()}
            >
              {submit.isPending ? t('experience.sending') : t('experience.send')}
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
              {t('experience.moderationNote')}
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  )
}
